// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import LivreService from '../../services/LivreService';

const LivresListItems = () => {
	const [originalLivres, setOriginalLivres] = useState([]);
	const [livres, setLivres] = useState([]);

	useEffect(() => {
		Promise.all([LivreService.getAllLivres()]).then(([LivresData]) => {
			setOriginalLivres(LivresData);
			setLivres(LivresData);
		});
	}, []);

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'titre' && value) {
				return value.toUpperCase();
			} else if (columnId === 'editeur' || (columnId === 'date_achat' && value)) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else {
				return value;
			}
		}
		return value;
	};

	const getInitials = (livre) => {
		const auteur = livre.auteur
			? livre.auteur.charAt(0).toUpperCase()
			: '';
		const titre = livre.titre ? livre.titre.charAt(0).toUpperCase() : '';
		return `${titre} ${auteur}`;
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'titre',
				Header: 'Titre',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					return (
						<div className="d-flex align-items-center">
							<Avatar
								type="initial"
								size="md"
								name={initials}
								className="rounded-circle"
								alt={initials}
							/>
							{value ? (
								<Link to={`/services/bibliotheque/catalogue/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'titre')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'auteur',
				Header: 'Auteur',
				Cell: ({ value }) => <div>{formatValue(value, 'auteur')}</div>
			},
			{
				accessor: 'editeur',
				Header: 'Editeur',
				Cell: ({ value }) => <div>{formatValue(value, 'editeur')}</div>
			},
			{
				accessor: 'date_achat',
				Header: "Date d'achat",
				Cell: ({ value }) => <div>{formatValue(value, 'date_achat')}</div>
			},
			{
				accessor: 'numero_appel',
				Header: "numero d'appel",
				Cell: ({ value }) => <div>{formatValue(value, 'numero_appel')}</div>
			},
			{
				accessor: 'cout',
				Header: 'Cout',
				Cell: ({ value }) => <div>{value}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return livres.map((livre) => ({
			...livre
		}));
	}, [livres]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xl={2} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un livre"
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default LivresListItems;
